import { readCssPixel } from "@albertine/design-system/utils/readCssPixel";

export { readCssPixel };

/**
 * Native chat writes the composer coverage into `--safe-area-inset-bottom`.
 * Concierge keeps reporting height through `chat.composer.state`.
 */
export function composerLayoutHeight({
    nativeInset,
    hostComposerHeight,
}: {
    nativeInset: number | null;
    hostComposerHeight: number;
}): number {
    return nativeInset ?? hostComposerHeight;
}

/** Matches the native composer animation, `.easeOut(duration: 0.3)`. */
export const composerInsetAnimationMs = 300;

/** CSS form of `composerInsetEaseOut`. */
export const composerInsetTimingFunction = "cubic-bezier(0.33, 1, 0.68, 1)";

export const composerInsetDurationVariable = "--chat-composer-inset-duration";

/** Transition for a property that follows the native composer inset. */
export function composerInsetTransition(property: string): string {
    return `${property} var(${composerInsetDurationVariable}, 0ms) ${composerInsetTimingFunction}`;
}

/**
 * Milliseconds the list should spend moving to the next inset.
 * A duration written by the shell wins, including zero. Without one, a single
 * jump uses the composer ease and a rapid small step snaps.
 */
export function composerInsetMotion({
    step,
    elapsedMs,
    authoredDurationMs,
    reduceMotion,
}: {
    step: number;
    elapsedMs: number;
    authoredDurationMs: number | null;
    reduceMotion: boolean;
}): number {
    if (Math.abs(step) < 0.5 || reduceMotion || authoredDurationMs === 0) {
        return 0;
    }
    if (authoredDurationMs) {
        return authoredDurationMs;
    }
    if (composerInsetTracksLiveFrame(elapsedMs, step)) {
        return 0;
    }
    return composerInsetAnimationMs;
}

/** `null` when the shell left the duration unset. `0` is an authored snap. */
export function readAuthoredInsetDuration(value: string): number | null {
    if (value.trim().length === 0) {
        return null;
    }
    return readCssPixel(value);
}

/**
 * A sample that arrives quickly, and only moves a little, is one frame of an
 * animation already running (the keyboard). A single jump should ease instead.
 */
export function composerInsetTracksLiveFrame(elapsedMs: number, step: number): boolean {
    return (
        elapsedMs >= 0 && elapsedMs < 50 && Math.abs(step) >= 0.5 && Math.abs(step) < 40
    );
}

/** How close to the latest message still counts as pinned to the composer. */
export const composerPinSlackPx = 48;

/**
 * Whether a bottom-inset change should keep the latest messages against the
 * composer. `distanceToBottom` is measured after the padding has updated.
 * A shrink that the browser already clamped still counts as pinned.
 */
export function shouldPinChatToComposer({
    distanceToBottom,
    delta,
    slack = composerPinSlackPx,
}: {
    distanceToBottom: number;
    delta: number;
    slack?: number;
}): boolean {
    if (Math.abs(delta) < 0.5) {
        return false;
    }
    const distanceBefore = distanceToBottom - delta;
    if (distanceBefore <= slack) {
        return true;
    }
    return delta < 0 && distanceToBottom <= slack;
}

/** Cubic ease-out. `progress` is clamped to 0...1. */
export function composerInsetEaseOut(progress: number): number {
    const t = Math.min(1, Math.max(0, progress));
    return 1 - (1 - t) ** 3;
}
